/**
 * This Vuex store contains state *about* the current cart, but purposefully does not contain the cart itself.
 * Components can watch or consume these state properties to react to changes in the cart, and can use the
 * DI container to access the current cart directly if needed, in response to these changes.
 */
export function createCartStore() {
    return {
        namespaced: true,

        /**
         * Each of these properties should be prefixed with `currentCart` so that when you use it in a Vue component
         * with `mapState`, it is clear that they are related to the cart, e.g. `currentCartHasLines`.
         * Otherwise, you'll have a property `hasLines` (for example) that is ambiguous in its meaning.
         */
        state: {
            /**
             * @type {boolean|null}
             */
            currentCartIsEditingOrder: null,

            /**
             * @type {boolean|null}
             */
            currentCartHasLines: null,

            /**
             * @type {string|null}
             */
            currentCartPaymentPattern: null,

            /**
             * @type {string|null}
             */
            currentCartThirdPartyRef: null,

            /**
             * This property name is an exception to the rule above, as we want to use this in watchers in
             * Vue components, and watching '$store.state.cart.currentCartModified' is awkwardly long.
             * With this name you can watch the property with '$store.state.cart.modified' which is cleaner.
             *
             * @type {number|null}
             */
            modified: null,
        },

        mutations: {
            /**
             * @param {object} state
             * @param {?boolean} value
             */
            setHasLines(state, value) {
                state.currentCartHasLines = value;
            },

            /**
             * @param {object} state
             * @param {?boolean} value
             */
            setIsEditingOrder(state, value) {
                state.currentCartIsEditingOrder = value;
            },

            /**
             * @param {object} state
             * @param {?number} value
             */
            setModified(state, value) {
                state.modified = value;
            },

            /**
             * @param {object} state
             * @param {?string} value
             */
            setPaymentPattern(state, value) {
                state.currentCartPaymentPattern = value;
            },

            /**
             * @param {object} state
             * @param {?string} value
             */
            setThirdPartyRef(state, value) {
                state.currentCartThirdPartyRef = value;
            },
        },
    };
}
